import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import SkeletonTable from "../../Common/SkeletonTable";
import ErrorCard from "../../Common/ErrorCard";
import EmptyCard from "../../Common/EmptyCard";
import PaginationBar from "../../Common/PaginationBar";
import { inventoryFetcher } from "../../../network/InventoryService";
import { getInventoryTypeTitle } from "../../../../../utilities/helpers";
import { DEFAULT_PAGE_SIZE } from "../../../utils/inventoryHelper";

const StockList = ({ searchTerm }) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchTerm) {
      params.append("search_term", searchTerm);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-stock?${params.toString()}`;
  }, [page, searchTerm]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const stockData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={6}
          rowCount={7}
          headers={[
            "#",
            "Lot ID",
            "Warehouse Name",
            "PO Number",
            "Warehouse Entry Date",
            "Inventory Count",
            "Inventory Type",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          title="Error loading stock data"
          message="Failed to load stock data. Please try again."
          onRetryClick={mutate}
        />
      );
    }

    if (stockData.length === 0) {
      return (
        <EmptyCard
          title="No stock data found"
          message={
            searchTerm
              ? "No stock data found matching your search criteria."
              : "No stock data available."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Warehouse Name</th>
            <th>PO Number</th>
            <th>Warehouse Entry Date</th>
            <th>Inventory Count</th>
            <th>Inventory Type</th>
          </tr>
        </thead>
        <tbody>
          {stockData.map((stock, index) => (
            <tr key={index} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td title={stock.warehouse__name || ""}>
                {stock.warehouse__name || "-"}
              </td>
              <td>{stock.po__po_number || "-"}</td>
              <td>{formatDate(stock.entry_date)}</td>
              <td>{stock.inventory_count?.toLocaleString() || 0}</td>
              <td>{getInventoryTypeTitle(stock.model__type)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inventory-content-container">
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </div>
  );
};

export default StockList;
